import { Modal, Text } from "@shopify/polaris"
import { observer } from "mobx-react"
import { parseRealPrice, ProTextField, validateV2 } from "polaris-enhance"
import { useEffect, useMemo, useState } from "react"

import APIPlan from "@/api/plan"
import stores from "@/stores"
import { formatDisplayNumber } from "@/utils/formatNumber"
import { openCustomURL } from "@/utils/location"

interface EditSpendingLimitModalProps {
  open: boolean
  onClose: () => void
}

const SPENDING_LIMIT_MIN = 1
const SPENDING_LIMIT_MAX = 20000

const clampSpendingLimit = (value: string) => {
  if (!value) return value
  let amount = Number(parseRealPrice(value))
  if (!Number.isFinite(amount)) return value
  if (amount < SPENDING_LIMIT_MIN) amount = SPENDING_LIMIT_MIN
  if (amount > SPENDING_LIMIT_MAX) amount = SPENDING_LIMIT_MAX
  return String(amount)
}

const getExtraReturnCount = (amount: number, onePrice: number) => {
  if (!amount || !onePrice) return 0
  return Math.floor(amount / onePrice)
}

const EditSpendingLimitModal = (props: EditSpendingLimitModalProps) => {
  const { open, onClose } = props
  const { PlanStore } = stores
  const userPkg = PlanStore.config?.user_pkg
  const onePrice = Number(PlanStore.completedCurPlan?.one_price ?? 0.45)

  const packageKind = userPkg?.package_kind ?? 0
  const packagePeriod = userPkg?.package_period ?? 1
  const initialLimit = Number(userPkg?.spend_limit ?? 0)

  const isFreePlan = packageKind <= 1
  const isAnnualPlan = packagePeriod === 2
  const isEditable = !isFreePlan && !isAnnualPlan

  const [inputValue, setInputValue] = useState("")
  const [loading, setLoading] = useState(false)
  const [isFocus, setIsFocus] = useState(false)

  useEffect(() => {
    if (!open) return
    setIsFocus(false)
    if (isEditable) {
      setInputValue(initialLimit > 0 ? String(initialLimit) : "")
      return
    }
    setInputValue("0.00")
  }, [open, isEditable, initialLimit])

  const parsedAmount = useMemo(() => {
    const amount = Number(parseRealPrice(inputValue))
    return Number.isFinite(amount) ? amount : 0
  }, [inputValue])

  const clampedAmount = useMemo(() => {
    if (!inputValue) return null
    const amount = Number(parseRealPrice(inputValue))
    if (!Number.isFinite(amount) || amount <= 0) return null
    return Math.min(SPENDING_LIMIT_MAX, Math.max(SPENDING_LIMIT_MIN, amount))
  }, [inputValue])

  const hasChanged = clampedAmount !== null && clampedAmount !== initialLimit

  const extraReturnCount = useMemo(() => {
    return getExtraReturnCount(parsedAmount, onePrice)
  }, [parsedAmount, onePrice])

  const displayValue = useMemo(() => {
    if (!isEditable || isFocus) return inputValue
    if (!inputValue) return inputValue
    return formatDisplayNumber(parsedAmount)
  }, [inputValue, isEditable, isFocus, parsedAmount])

  const planNotice = isFreePlan
    ? t("SpendingLimitFreePlanNotice")
    : isAnnualPlan
      ? t("SpendingLimitAnnualPlanNotice")
      : null

  const helperText = isEditable
    ? t("SpendingLimitExtraRequestsHint", {
        replace: {
          count: formatDisplayNumber(extraReturnCount),
        },
      })
    : t("SpendingLimitZeroExtraRequestsHint")

  const handleSave = async () => {
    if (!isEditable || !hasChanged || clampedAmount === null) return

    if (!validateV2().allPass) return

    setLoading(true)
    const res = await APIPlan.updateSpendLimit(clampedAmount)

    if (res.code === 1000) {
      openCustomURL(res?.data?.confirmation_url ?? "", false)
    }
    setLoading(false)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("EditSpendingLimit")}
      primaryAction={{
        content: t("Save"),
        onAction: handleSave,
        loading,
        disabled: !isEditable || loading || !hasChanged,
      }}
      secondaryActions={[
        {
          content: t("Cancel"),
          onAction: onClose,
        },
      ]}
    >
      <Modal.Section>
        <div className="space-y-3">
          <Text variant="bodyMd" as="p">
            {t("SpendingLimitDescription")}
          </Text>
          {planNotice ? (
            <Text variant="bodyMd" as="p">
              {planNotice}
            </Text>
          ) : null}
          <ProTextField
            label={t("SpendingLimit")}
            value={displayValue}
            onChange={(value) => {
              setInputValue(value.replace(/[^\d.]/g, ""))
            }}
            onFocus={() => setIsFocus(true)}
            onBlur={() => {
              setIsFocus(false)
              if (!inputValue) return
              setInputValue(clampSpendingLimit(inputValue))
            }}
            suffix="USD"
            disabled={!isEditable}
            rules={[
              {
                required: true,
                message: t("Required"),
              },
              {
                validator(rule, val) {
                  const amount = Number(val)
                  if (amount < SPENDING_LIMIT_MIN) {
                    return t("Required")
                  }
                  if (amount > SPENDING_LIMIT_MAX) {
                    return t("Required")
                  }
                },
              },
            ]}
            helpText={helperText}
          />
        </div>
      </Modal.Section>
    </Modal>
  )
}

export default observer(EditSpendingLimitModal)
